// Allowances tab: per-jurisdiction allowance and standard-deduction
// amounts, with create/remove. Reads and writes only the confirmed
// allowance-config endpoints.
//
// Delete confirmation goes through the shared Dialog. The allowance
// contract has no active/inactive flag and no update endpoint, so there is
// no toggle to show here — rows are simply created or removed.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import {
  type AllowanceConfig,
  createAllowanceConfig,
  deleteAllowanceConfig,
  listAllowanceConfigs,
} from "@/lib/api";
import { humanizeLabel } from "@/lib/labels";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function toMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) ? minor : null;
}

function placeLabel(config: AllowanceConfig): string {
  return humanizeLabel(config.jurisdiction_level);
}

const LEVELS = ["federal", "state", "local"] as const;

export function AllowancesTab() {
  const [configs, setConfigs] = useState<AllowanceConfig[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<AllowanceConfig | null>(
    null,
  );

  const [level, setLevel] = useState("federal");
  const [jurisdiction, setJurisdiction] = useState("");
  const [taxYear, setTaxYear] = useState(String(new Date().getFullYear()));
  const [allowance, setAllowance] = useState("");
  const [deduction, setDeduction] = useState("");

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    listAllowanceConfigs()
      .then(setConfigs)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(reload, [reload]);

  const submitCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const allowance_value_cents = toMinor(allowance);
    const standard_deduction_cents = toMinor(deduction);
    const parsedYear = Number(taxYear);
    if (
      !Number.isInteger(parsedYear) ||
      parsedYear < 2000 ||
      parsedYear > 2100
    ) {
      setError("Enter a tax year like 2026.");
      return;
    }
    if (allowance_value_cents === null) {
      setError("Enter the allowance amount (zero or more).");
      return;
    }
    if (standard_deduction_cents === null) {
      setError("Enter the standard deduction (zero or more).");
      return;
    }
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      await createAllowanceConfig({
        jurisdiction_level: level,
        ...(jurisdiction.trim() ? { jurisdiction: jurisdiction.trim() } : {}),
        tax_year: parsedYear,
        allowance_value_cents,
        standard_deduction_cents,
      });
      setNotice(`Saved allowances for ${parsedYear}.`);
      setJurisdiction("");
      setAllowance("");
      setDeduction("");
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (config: AllowanceConfig) => {
    setError(null);
    setNotice(null);
    try {
      await deleteAllowanceConfig(config.id);
      setNotice(`Removed the ${config.tax_year} allowances.`);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    }
  };

  return (
    <div className="grid gap-4">
      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-3">
            <Button type="button" variant="secondary" onClick={reload}>
              Retry
            </Button>
          </div>
        </div>
      ) : null}
      {notice ? (
        <output className="surface block rounded-xl border border-[var(--status-success-foreground)] p-4 text-sm">
          {notice}
        </output>
      ) : null}

      <form
        onSubmit={submitCreate}
        className="surface grid gap-3 rounded-2xl p-5"
      >
        <h3 className="text-sm font-semibold">Set allowances</h3>
        <div className="flex flex-wrap items-end gap-3">
          <label htmlFor="ta-level" className="grid gap-1 text-xs font-medium">
            Level
            <select
              id="ta-level"
              className="select"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              disabled={creating}
            >
              {LEVELS.map((value) => (
                <option key={value} value={value}>
                  {humanizeLabel(value)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="ta-jur" className="grid gap-1 text-xs font-medium">
            Place (optional)
            <Input
              id="ta-jur"
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              placeholder="US-CA"
              disabled={creating}
            />
          </label>
          <label htmlFor="ta-year" className="grid gap-1 text-xs font-medium">
            Tax year
            <Input
              id="ta-year"
              type="number"
              value={taxYear}
              onChange={(e) => setTaxYear(e.target.value)}
              required
              disabled={creating}
            />
          </label>
          <label htmlFor="ta-allow" className="grid gap-1 text-xs font-medium">
            Allowance ({DISPLAY_CURRENCY})
            <Input
              id="ta-allow"
              type="number"
              min="0"
              step="0.01"
              value={allowance}
              onChange={(e) => setAllowance(e.target.value)}
              placeholder="0.00"
              disabled={creating}
            />
          </label>
          <label htmlFor="ta-deduct" className="grid gap-1 text-xs font-medium">
            Standard deduction ({DISPLAY_CURRENCY})
            <Input
              id="ta-deduct"
              type="number"
              min="0"
              step="0.01"
              value={deduction}
              onChange={(e) => setDeduction(e.target.value)}
              placeholder="0.00"
              disabled={creating}
            />
          </label>
          <Button type="submit" disabled={creating}>
            {creating ? "Saving…" : "Save allowances"}
          </Button>
        </div>
      </form>

      {loading && configs === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : !error && configs !== null && configs.length === 0 ? (
        <EmptyState
          title="No allowances set"
          reason="Save the tax-free amount and standard deduction above — one row per place and year."
        />
      ) : configs !== null && configs.length > 0 ? (
        <>
          <div
            className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
            data-testid="tax-allowances-table"
          >
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Place</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead className="text-right">Allowance</TableHead>
                    <TableHead className="text-right">
                      Standard deduction
                    </TableHead>
                    <TableHead>
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {configs.map((config) => (
                    <TableRow key={config.id}>
                      <TableCell className="font-medium">
                        {placeLabel(config)}
                        {config.jurisdiction ? (
                          <span className="ml-2 font-mono text-xs text-[var(--muted-foreground)]">
                            {config.jurisdiction}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {config.tax_year}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {money(config.allowance_value_cents)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {money(config.standard_deduction_cents)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => setConfirmRemove(config)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div
            className="stack-list grid gap-3 min-[64rem]:hidden"
            data-testid="tax-allowances-stack"
          >
            {configs.map((config) => (
              <article
                key={config.id}
                className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
              >
                <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                  <span className="record-title font-medium">
                    {placeLabel(config)}
                  </span>
                  <span className="tabular-nums">{config.tax_year}</span>
                </div>
                {config.jurisdiction ? (
                  <CardField label="Place code">
                    <span className="font-mono text-xs">
                      {config.jurisdiction}
                    </span>
                  </CardField>
                ) : null}
                <CardField label="Allowance">
                  <span className="tabular-nums">
                    {money(config.allowance_value_cents)}
                  </span>
                </CardField>
                <CardField label="Standard deduction">
                  <span className="tabular-nums">
                    {money(config.standard_deduction_cents)}
                  </span>
                </CardField>
                <div className="flex flex-wrap gap-2 pt-3">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirmRemove(config)}
                  >
                    Remove
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </>
      ) : null}

      <Dialog
        open={confirmRemove !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRemove(null);
        }}
        title={
          confirmRemove
            ? `Remove the ${confirmRemove.tax_year} ${placeLabel(confirmRemove)} allowances?`
            : "Remove allowances?"
        }
        description={
          confirmRemove
            ? `Deletes the allowance (${money(confirmRemove.allowance_value_cents)}) and standard deduction (${money(confirmRemove.standard_deduction_cents)}) for ${confirmRemove.jurisdiction ?? "all places"} in ${confirmRemove.tax_year}.`
            : undefined
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmRemove(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              const config = confirmRemove;
              setConfirmRemove(null);
              if (config) void remove(config);
            }}
          >
            Remove allowances
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
